/**
 * Teen Patti 20-20 — the game page. PHASE D (2026-09-06): on the real money
 * path. The import below is the whole swap — `ServerTableClock` fulfils the
 * same `TableClock` interface the Phase-A practice clock did, so every phase
 * still derives from the clock's round and the page only plays it. `?practice=1`
 * hands back the local clock for the dev sandbox.
 *
 * ONE decision per round: PLAYER A or PLAYER B (X1.98 total return, same
 * hand = PUSH, stake back). Gold ring = the side you chose. The ranking
 * ladder across the top shows where each hand stands as its cards turn.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type Card, type Rank, rankValue } from '../_cards/deck';
import { PIP_DEFS, faceSvg, backSvg } from '../_cards/art';
import { type Side, type Category, rankHand, PAYOUT, fmtX } from './engine';
import { ServerTableClock as LocalTableClock, type TableClock, type Round, type Wallet, FLIP_MS } from './serverTableClock';
import { crowdFor, seatsFor, mask } from './fakes';
import { FELT_SVG, SHOE_SVG, CROWN_SVG, FILIGREE_SVG, LADDER_SVG } from './art';
import {
  initTpSound, isTpSoundOn, setTpSoundOn, unlockTpSound, initTpMusic, isTpMusicOn, setTpMusicOn,
  startTpMusic, stopTpMusic, bindTpVisibility, tpBedPhase,
  sShuffle, sCountTick, sKnock, sFlip, sRankUp, sStamp, sWin, sLoss, sPush, sSatOut, sChip, sUndo, sDenied, sTap, sCrowd,
} from './sound';
import './teen.css';

const armed = new WeakMap<Element, number>();
function press(fn: () => void) {
  return {
    onPointerDown: (e: React.PointerEvent) => { e.stopPropagation(); armed.set(e.currentTarget, e.pointerId); },
    onPointerUp: (e: React.PointerEvent) => { e.stopPropagation(); if (armed.get(e.currentTarget) !== e.pointerId) return; armed.delete(e.currentTarget); if ((e.currentTarget as HTMLButtonElement).disabled) return; fn(); },
    onPointerCancel: (e: React.PointerEvent) => { armed.delete(e.currentTarget); },
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key !== 'Enter' && e.key !== ' ') return; if (e.repeat) return; e.preventDefault(); e.stopPropagation(); if ((e.currentTarget as HTMLButtonElement).disabled) return; fn(); },
  };
}

// Opening balance BEFORE the server has spoken. It used to be 10,000 — the Phase-A practice
// bankroll — which meant a real-money table showed the player ₹10,000 they did not have until
// the first `/state` landed, and `bet()` gated affordability on that fiction. Zero plus the
// `walletReady` gate below means nothing is ever shown or spent against a number we invented.
// (2026-09-06; the practice clock funds itself, so it is unaffected.)
const PRACTICE_START = 0;
const CHIPS = [10, 50, 100, 500, 1000, 5000];
const MAX_SIDE = 5000;
const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`;
const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private */ } };
const SIDE_NAME: Record<Side, string> = { a: 'PLAYER A', b: 'PLAYER B' };
const SIDES: Side[] = ['a', 'b'];
const RUNGS = ['TRAIL', 'PURE\nSEQ', 'SEQ', 'COLOUR', 'PAIR', 'HIGH'];
const RUNG_X = [9.9, 25.9, 42.0, 58.0, 74.1, 90.1];          // rung centres, % of the ladder width
const rungOf = (cat: number) => 7 - cat;                     // cat 6 (trail) = rung 1
const FLY_MS = 280;

const hiVal = (r: Rank) => (r === 'A' ? 14 : rankValue(r));
const WORD: Partial<Record<Rank, string>> = { A: 'ACE', K: 'KING', Q: 'QUEEN', J: 'JACK' };
const rw = (r: Rank) => WORD[r] ?? r;
const plural = (r: Rank) => (r === '6' ? '6ES' : `${rw(r)}S`);
const seqText = (desc: Card[]) => {
  const rs = desc.map((c) => c.rank);
  if (rs[0] === 'A' && rs[1] === '3' && rs[2] === '2') return 'A-2-3';
  return rs.join('-');
};
/** what the plate under a hand says, with only the cards shown so far */
function describe(cards: Card[]): { cat: Category | 0; text: string } {
  if (cards.length === 0) return { cat: 0, text: '· · ·' };
  const desc = [...cards].sort((a, b) => hiVal(b.rank) - hiVal(a.rank));
  if (cards.length < 3) {
    if (cards.length === 2 && cards[0].rank === cards[1].rank) return { cat: 2, text: `PAIR OF ${plural(cards[0].rank)}` };
    return { cat: 1, text: `${rw(desc[0].rank)} HIGH` };
  }
  const h = rankHand(cards);
  switch (h.cat) {
    case 6: return { cat: 6, text: `TRAIL OF ${plural(desc[0].rank)}` };
    case 5: return { cat: 5, text: `PURE SEQ · ${seqText(desc)}` };
    case 4: return { cat: 4, text: `SEQ · ${seqText(desc)}` };
    case 3: return { cat: 3, text: `COLOUR · ${rw(desc[0].rank)} HIGH` };
    case 2: { const p = desc.find((c, i) => desc[i + 1]?.rank === c.rank) ?? desc[0]; return { cat: 2, text: `PAIR OF ${plural(p.rank)}` }; }
    default: return { cat: 1, text: `${rw(desc[0].rank)} HIGH` };
  }
}

type Strip = { text: string; cls: string };

/** a card: back + face stacked; flips via the host classes */
const CardEl = ({ card, cls, style }: { card: Card; cls: string; style?: React.CSSProperties }) => (
  <div className={`tp-card ${cls}`} style={style}>
    <span className="tp-face--back" dangerouslySetInnerHTML={{ __html: backSvg() }} />
    <span className="tp-face--front" dangerouslySetInnerHTML={{ __html: faceSvg(card.rank, card.suit) }} />
    <span className="tp-face--edge"><i /></span>
    <span className="tp-cring" />
  </div>
);

const TpPage = () => {
  const appRef = useRef<HTMLDivElement>(null);
  const shoeRef = useRef<HTMLDivElement>(null);
  const fanRefs = useRef<Record<Side, HTMLDivElement | null>>({ a: null, b: null });
  const balRef = useRef<HTMLSpanElement>(null);

  const balanceRef = useRef(PRACTICE_START);
  const [balance, setBalance] = useState(PRACTICE_START);
  const [balFx, setBalFx] = useState('');
  const wallet = useMemo<Wallet>(() => ({
    balance: () => balanceRef.current,
    debit: (n) => { if (balanceRef.current < n) return false; balanceRef.current -= n; setBalance(balanceRef.current); setBalFx('dip'); window.setTimeout(() => setBalFx(''), 300); return true; },
    credit: (n) => { balanceRef.current += n; setBalance(balanceRef.current); },
  }), []);

  const clockRef = useRef<TableClock | null>(null);
  const [, setTick] = useState(0);
  // Re-read every tick: the clock flips this the moment its first /state (or a bet ack) lands.
  const walletReady = clockRef.current?.walletReady() ?? false;
  const [chip, setChip] = useState(() => { const v = Number(lsGet('tp.chip')); return CHIPS.includes(v) ? v : 100; });
  const [lastBet, setLastBet] = useState<{ side: Side; amount: number } | null>(() => { try { const v = JSON.parse(lsGet('tp.last') || 'null'); return v && v.side && v.amount ? v : null; } catch { return null; } });
  const [soundOn, setSoundOn] = useState(initTpSound);
  const [musicOn, setMusicOn] = useState(initTpMusic);
  const [event, setEvent] = useState<(Strip & { until: number }) | null>(null);
  const [countdown, setCountdown] = useState(15);
  const [hint, setHint] = useState(() => lsGet('tp.seen') !== '1');
  const [rulesOpen, setRulesOpen] = useState(false);
  const [coins, setCoins] = useState<{ id: number; cx: number; cy: number }[]>([]);
  const [rebetBlink, setRebetBlink] = useState(false);
  const [stampOut, setStampOut] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [handUp, setHandUp] = useState<Record<Side, number>>({ a: 0, b: 0 });

  const prevStatusRef = useRef<Round['status'] | 'boot'>('boot');
  const shownRef = useRef(0);
  const catRef = useRef<Record<Side, number>>({ a: 0, b: 0 });
  const timersRef = useRef<number[]>([]);
  const later = useCallback((fn: () => void, ms: number) => { timersRef.current.push(window.setTimeout(fn, ms)); }, []);
  const flyRef = useRef<Map<string, { fx: number; fy: number }>>(new Map());
  const revealedCrowdRef = useRef(0);
  const [crowdRows, setCrowdRows] = useState<string[]>([]);

  useLayoutEffect(() => {
    const el = appRef.current; if (!el) return;
    const write = (w: number, h: number) => { el.style.setProperty('--tp-w', `${Math.round(w)}px`); el.style.setProperty('--tp-h', `${Math.round(h)}px`); };
    const cs = getComputedStyle(el);
    write(el.clientWidth, el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom));
    const ro = new ResizeObserver((es) => { const r = es[es.length - 1].contentRect; write(r.width, r.height); });
    ro.observe(el, { box: 'content-box' });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let seed = Number(lsGet('tp_table_seed'));
    if (!seed) { seed = (Math.random() * 0xffffffff) >>> 0; lsSet('tp_table_seed', String(seed)); }
    const clock = new LocalTableClock(seed, wallet, 300 + (seed % 90));
    clockRef.current = clock;
    const unsub = clock.subscribe(() => setTick((t) => t + 1));
    setTick((t) => t + 1);                                  // the first render must see the round
    if (isTpMusicOn() && isTpSoundOn()) startTpMusic();
    const unbind = bindTpVisibility();
    return () => { unsub(); clock.destroy(); unbind(); stopTpMusic(); timersRef.current.forEach(window.clearTimeout); };
  }, [wallet]);

  const clock = clockRef.current;
  const r = clock?.round();
  const st = r?.status ?? 'betting';
  const bets = clock?.myBets() ?? [];
  const mySide: Side | null = bets.length ? bets[0].side : null;
  const myTotal = bets.reduce((s, b) => s + b.amount, 0);
  const outcome = clock?.myOutcome() ?? null;

  const showEvent = useCallback((text: string, cls: string, hold = 1500) => {
    setEvent({ text, cls, until: performance.now() + hold });
    later(() => setEvent((e) => (e && performance.now() >= e.until - 5 ? null : e)), hold + 10);
  }, [later]);

  /* geometry for flights: shoe mouth → a fan slot, in px */
  const W = appRef.current?.clientWidth ?? 360;
  const SLOT_LEFT = [0.005 * W, 0.10 * W, 0.195 * W];
  const flightTo = (side: Side, slot: number): { fx: number; fy: number } => {
    const s = shoeRef.current, fan = fanRefs.current[side]; if (!s || !fan) return { fx: 0, fy: 0 };
    const sr = s.getBoundingClientRect(), fr = fan.getBoundingClientRect();
    return { fx: sr.left + sr.width * 0.5 - fr.left - SLOT_LEFT[slot], fy: sr.top + sr.height * 0.55 - fr.top - 0.014 * W };
  };

  /* ── phase transitions ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!clock || !r) return;
    if (st === prevStatusRef.current) return;
    const prev = prevStatusRef.current;
    prevStatusRef.current = st;
    if (st === 'betting') {
      shownRef.current = 0; catRef.current = { a: 0, b: 0 }; flyRef.current.clear();
      setSweeping(false); setStampOut(false);
      revealedCrowdRef.current = 0; setCrowdRows([]);
      if (prev !== 'boot') sShuffle();
      tpBedPhase('betting');
      setEvent(null);
    }
    if (st === 'locked') { sKnock(); tpBedPhase('dealing', 0); }
    if (st === 'result' && r.winner) {
      const w = r.winner;
      later(() => { sStamp(); }, 250);
      later(() => {
        if (!outcome || outcome.staked === 0) { if (w === 'push') sPush(); else sSatOut(); }
        else if (w === 'push') sPush();
        else if (outcome.returned > 0) { sWin(outcome.returned); setBalFx('up'); later(() => setBalFx(''), 900); fireCoins(w); }
        else sLoss();
      }, 550);
      tpBedPhase('result');
      if (lastBet == null && bets.length) { setRebetBlink(true); later(() => setRebetBlink(false), 1000); }
      if (bets.length) { const lb = { side: bets[0].side, amount: myTotal }; setLastBet(lb); lsSet('tp.last', JSON.stringify(lb)); }
    }
    if (st === 'intermission') {
      tpBedPhase('intermission');
      later(() => setSweeping(true), 100);
      later(() => setStampOut(true), 2700);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st, r?.no]);

  /* revealed cards: flip sound on landing, rank-up chime when a hand climbs */
  const shownA = r?.shownA ?? [], shownB = r?.shownB ?? [];
  useEffect(() => {
    if (!r || st !== 'revealing') return;
    const n = shownA.length + shownB.length;
    if (n > shownRef.current) {
      shownRef.current = n;
      const side: Side = n % 2 === 1 ? 'a' : 'b';
      later(() => sFlip(n, side), FLY_MS);
      const d = describe(side === 'a' ? shownA : shownB);
      if (d.cat > catRef.current[side]) {
        catRef.current[side] = d.cat;
        if (d.cat >= 2) later(() => { sRankUp(d.cat); setHandUp((h) => ({ ...h, [side]: h[side] + 1 })); }, FLY_MS + 240);
      }
      tpBedPhase('dealing', n / 6);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownA.length, shownB.length, st]);

  /* countdown + ticks + crowd during betting */
  useEffect(() => {
    if (st !== 'betting' || !r) return;
    let lastWhole = -1;
    const tick = () => {
      const left = Math.max(0, Math.ceil((r.locksAt - performance.now()) / 1000));
      setCountdown(left);
      if (left !== lastWhole) { lastWhole = left; if (left <= 3 && left >= 1) sCountTick(left); }
      const t = (performance.now() - r.opensAt) / 1000;
      const crowd = crowdFor(r.fakeSeed, r.no);
      while (revealedCrowdRef.current < crowd.length && crowd[revealedCrowdRef.current].at <= t) {
        const f = crowd[revealedCrowdRef.current++];
        setCrowdRows((rows) => [`${f.name} put ${inr(f.stake)} on ${SIDE_NAME[f.side]}`, ...rows].slice(0, 10));
        sCrowd();
      }
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [st, r]);

  const fireCoins = (winner: Side) => {
    const app = appRef.current, bal = balRef.current, mat = document.querySelector(`.tp-mat--${winner}`) as HTMLElement | null;
    if (!app || !bal || !mat) return;
    const ar = app.getBoundingClientRect(), br = bal.getBoundingClientRect(), xr = mat.getBoundingClientRect();
    app.style.setProperty('--tp-wx', `${(br.left - ar.left + br.width / 2).toFixed(0)}px`);
    app.style.setProperty('--tp-wy', `${(br.top - ar.top + br.height / 2).toFixed(0)}px`);
    const id = Date.now();
    setCoins((c) => [...c, { id, cx: xr.left - ar.left + xr.width / 2, cy: xr.top - ar.top + xr.height / 2 }]);
    later(() => setCoins((c) => c.filter((x) => x.id !== id)), 1200);
  };

  /* ── actions ───────────────────────────────────────────────────────── */
  const place = (side: Side) => {
    const c = clockRef.current; if (!c || !r) return;
    unlockTpSound();
    // Until the server has told us the balance we cannot say whether a stake is affordable, so we
    // do not let one be composed — the alternative is a bet the server refuses for a reason the
    // player was never shown. (2026-09-06)
    if (!c.walletReady()) { showEvent('LOADING YOUR BALANCE…', 'err'); sDenied(); return; }
    if (st !== 'betting') { showEvent('TOO LATE · BETS CLOSED', 'err'); sDenied(); return; }
    if (mySide && mySide !== side) {
      /* one side per round: the whole stake moves and the chip is added */
      const total = myTotal + chip;
      if (total > MAX_SIDE) { showEvent(`MAX ${inr(MAX_SIDE)} ON A PLAYER`, 'err'); sDenied(); return; }
      if (chip > balanceRef.current) { showEvent('NOT ENOUGH BALANCE · TAP ₹ TO ADD', 'err'); sDenied(); return; }
      c.unbet(); const res = c.bet(side, total);
      // A REFUSED SWITCH MUST SAY SO (2026-09-06). This used to be `if (res.ok)` with no else,
      // so a refusal took the chips off the table in silence — no toast, no sound — and the
      // player was left looking at an empty side wondering what they had done wrong.
      if (res.ok === false) { showEvent(res.reason === 'balance' ? 'NOT ENOUGH BALANCE · TAP ₹ TO ADD' : 'TOO LATE · BETS CLOSED', 'err'); sDenied(); }
      else { sChip(); showEvent(`${inr(total)} MOVED TO ${SIDE_NAME[side]} ✓ · ${countdown}s`, 'set', 1200); }
      return;
    }
    if (myTotal + chip > MAX_SIDE) { showEvent(`MAX ${inr(MAX_SIDE)} ON A PLAYER`, 'err'); sDenied(); return; }
    const res = c.bet(side, chip);                                   // PHASE-2 SEAM
    if (res.ok === false) { showEvent(res.reason === 'balance' ? 'NOT ENOUGH BALANCE · TAP ₹ TO ADD' : 'TOO LATE · BETS CLOSED', 'err'); sDenied(); return; }
    sChip(); navigator.vibrate?.(8);
    if (hint) { setHint(false); lsSet('tp.seen', '1'); }
  };
  const undoAll = () => {
    const c = clockRef.current; if (!c) return;
    const n = c.unbet();                                            // PHASE-2 SEAM: /unbet (one-shot per round)
    if (n > 0) { sUndo(); showEvent(`${inr(n)} BACK IN WALLET`, 'set', 1200); }
  };
  const rebet = () => {
    const c = clockRef.current; if (!c || !lastBet || st !== 'betting' || myTotal > 0) return;
    if (!c.walletReady()) { showEvent('LOADING YOUR BALANCE…', 'err'); sDenied(); return; }
    const res = c.bet(lastBet.side, lastBet.amount);
    if (res.ok === false) { showEvent('NOT ENOUGH BALANCE · TAP ₹ TO ADD', 'err'); sDenied(); return; }
    sChip();
  };

  /* ── derived ───────────────────────────────────────────────────────── */
  const winner = r?.winner ?? null;
  const betting = st === 'betting';
  const isResult = st === 'result' || st === 'intermission';
  const shownOf = (side: Side) => (side === 'a' ? shownA : shownB);
  const hand = { a: describe(shownA), b: describe(shownB) };
  const litRung = isResult && winner ? (winner === 'push' ? rungOf(r?.rankA?.cat ?? 1) : rungOf((winner === 'a' ? r?.rankA : r?.rankB)?.cat ?? 1)) : 0;
  const seats = r ? seatsFor(r.fakeSeed, r.no) : 120;
  const pot = r ? crowdFor(r.fakeSeed, r.no).reduce((s, f) => s + f.stake, 0) + myTotal : 0;
  const hist = clock?.history().slice(0, 12).reverse() ?? [];
  const revealed = shownA.length + shownB.length;

  const strip: Strip = (() => {
    if (event && performance.now() < event.until) return event;
    const hurry = countdown <= 3 ? ' hurry' : '';
    switch (st) {
      case 'betting': return mySide ? { text: `${inr(myTotal)} ON ${SIDE_NAME[mySide]} ✓ · ${countdown}s`, cls: 'set' + hurry } : { text: `PICK PLAYER A OR B · ${countdown}s`, cls: 'pick' + hurry };
      case 'locked': return { text: 'BETS CLOSED — DEALING', cls: 'closed' };
      case 'revealing': return revealed ? { text: `CARD ${revealed} OF 6…`, cls: 'deal' } : { text: 'BETS CLOSED — DEALING', cls: 'closed' };
      case 'result': {
        if (winner === 'push') return outcome && outcome.staked > 0 ? { text: `PUSH · ${inr(outcome.returned)} BACK TO YOU`, cls: 'out' } : { text: 'PUSH · SAME HAND', cls: 'out' };
        const w = winner ? SIDE_NAME[winner] : '';
        if (!outcome || outcome.staked === 0) return { text: `${w} WINS · YOU SAT OUT`, cls: 'out' };
        return outcome.returned > 0 ? { text: `${w} WINS · YOU WON ${inr(outcome.returned)}`, cls: 'win' } : { text: `${w} WINS · YOU LOST ${inr(outcome.staked)}`, cls: 'lose' };
      }
      case 'intermission': return { text: `NEXT ROUND IN ${Math.max(1, Math.ceil(((r?.nextOpensAt ?? 0) - performance.now()) / 1000))}s`, cls: 'next' };
    }
  })();

  const sideCls = (side: Side) => {
    const mine = mySide === side;
    if (isResult && winner) return winner === 'push' ? 'push' : winner === side ? 'won' : 'lost';
    if (st === 'locked' || st === 'revealing') return 'dim' + (mine ? ' mine' : '');
    return (mine ? 'mine' : '') + (betting && countdown <= 3 ? ' hurry' : '');
  };
  const pill = (side: Side) => {
    if (mySide !== side || myTotal === 0) return { cls: '', text: '' };
    if (isResult && outcome) {
      if (winner === 'push') return { cls: 'on', text: `${inr(outcome.returned)} BACK` };
      return outcome.returned > 0 ? { cls: 'on win', text: `+${inr(outcome.returned)}` } : { cls: 'on lose', text: `−${inr(outcome.staked)}` };
    }
    return { cls: 'on', text: inr(myTotal) };
  };
  const stampText = winner === 'push' ? { big: 'PUSH', small: 'SAME HAND · STAKES BACK' } : winner ? { big: `${SIDE_NAME[winner]} WINS`, small: (winner === 'a' ? r?.rankA : r?.rankB)?.name ?? '' } : null;

  return (
    <div className="tp-root fixed inset-0 z-[9999]">
    <div ref={appRef} className="tp-app" onPointerDown={unlockTpSound}>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden dangerouslySetInnerHTML={{ __html: `<defs>${PIP_DEFS}</defs>` }} />

      <div className="tp-ribbon">
        <div className="tp-title"><b>TEEN PATTI 20-20</b><span className="tp-live"><i />LIVE · ROUND #{r?.no ?? 0}</span></div>
        <button type="button" className={`tp-iconbtn ${soundOn ? '' : 'off'}`} aria-label="Sounds" {...press(() => setSoundOn(setTpSoundOn(!isTpSoundOn())))}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M4 9v6h4l5 5V4L8 9H4z" /><path d="M16.5 12a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z" /></svg>
        </button>
        <button type="button" className={`tp-iconbtn ${musicOn ? '' : 'off'}`} aria-label="Music" {...press(() => setMusicOn(setTpMusicOn(!isTpMusicOn())))}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M9 17.2V6.3l10-2v10.9h-2V6.7l-6 1.2v9.3z" /><ellipse cx="7" cy="17.6" rx="2.9" ry="2.4" /><ellipse cx="17" cy="15.6" rx="2.9" ry="2.4" /></svg>
        </button>
        <span ref={balRef} className={`tp-bal tp-num ${balFx}`} title={walletReady ? 'Your balance' : 'Loading your balance'}>{walletReady ? inr(balance) : '₹—'}</span>
      </div>

      <div className="tp-hist">
        <em>LAST 12</em>
        {hist.map((h, i) => <span key={`${r?.no}-${i}`} className={`tp-dot tp-dot--${h.winner} ${i === hist.length - 1 && isResult ? 'new' : ''}`}>{h.winner === 'a' ? 'A' : h.winner === 'b' ? 'B' : '='}</span>)}
      </div>

      <div className="tp-stage">
        <div className="tp-felt" dangerouslySetInnerHTML={{ __html: FELT_SVG }} />
        <div className="tp-abs tp-pot tp-num">POT {inr(pot)} · {seats} PLAYING</div>
        {st !== 'intermission' && (
          <div className={`tp-abs tp-you tp-num ${mySide ?? ''}`}><b>YOU</b>{mySide ? `${inr(myTotal)} · ${SIDE_NAME[mySide]}` : 'NO BET'}</div>
        )}

        <div className="tp-abs tp-ladder" data-lit={litRung || undefined}>
          <span className="tp-ladder-art" dangerouslySetInnerHTML={{ __html: LADDER_SVG }} />
          {RUNGS.map((name, i) => <b key={name} className={`tp-rung-label ${litRung === i + 1 ? 'lit' : ''}`} style={{ left: `${RUNG_X[i]}%` }}>{name}</b>)}
          {SIDES.map((side) => {
            const cat = hand[side].cat;
            const dimmed = isResult && winner && winner !== 'push' && winner !== side;
            return <i key={side} className={`tp-mark tp-mark--${side} ${cat ? 'on' : ''} ${dimmed ? 'dim' : ''}`} style={{ left: `${RUNG_X[(cat ? rungOf(cat) : 6) - 1]}%` }} />;
          })}
        </div>

        <div ref={shoeRef} className="tp-abs tp-shoe" dangerouslySetInnerHTML={{ __html: SHOE_SVG }} />
        <div className="tp-abs tp-vs">VS</div>

        {SIDES.map((side) => {
          const cards = shownOf(side);
          const cls = sideCls(side);
          const isWinner = isResult && winner === side;
          return (
            <button key={side} type="button" className={`tp-abs tp-mat tp-mat--${side} ${cls}`} {...press(() => place(side))}>
              <span className="tp-ignite" />
              <span className="tp-fili tp-fili--tl" dangerouslySetInnerHTML={{ __html: FILIGREE_SVG }} />
              <span className="tp-fili tp-fili--tr" dangerouslySetInnerHTML={{ __html: FILIGREE_SVG }} />
              <span className="tp-fili tp-fili--br" dangerouslySetInnerHTML={{ __html: FILIGREE_SVG }} />
              <span className="tp-fili tp-fili--bl" dangerouslySetInnerHTML={{ __html: FILIGREE_SVG }} />
              <span className="tp-mat-head"><b>{SIDE_NAME[side]}</b><i className="tp-num">{fmtX(PAYOUT)}</i></span>
              <span className={`tp-stake tp-num ${pill(side).cls}`}>{pill(side).text}</span>
              <div ref={(el) => { fanRefs.current[side] = el; }} className="tp-fan">
                {[0, 1, 2].map((s) => <span key={s} className={`tp-slot tp-slot--${s}`} />)}
                {cards.map((c, slot) => {
                  const key = `${side}${slot}`;
                  let f = flyRef.current.get(key);
                  if (!f) { f = flightTo(side, slot); flyRef.current.set(key, f); }
                  const ccls = `tp-card--dealt flip ${sweeping ? 'sweep' : 'deal'}`;
                  return <CardEl key={`${r?.no}-${key}`} card={c} cls={ccls} style={{ zIndex: slot + 1, ['--tp-left' as string]: `${SLOT_LEFT[slot].toFixed(1)}px`, ['--tp-rot' as string]: `${(slot - 1) * 9}deg`, ['--tp-fx' as string]: `${f.fx.toFixed(0)}px`, ['--tp-fy' as string]: `${f.fy.toFixed(0)}px`, ['--tp-si' as string]: 2 - slot }} />;
                })}
              </div>
              <em key={`h-${side}-${handUp[side]}`} className={`tp-hand ${handUp[side] ? 'up' : ''}`}>{hand[side].text}</em>
              {isWinner && !sweeping && <span className="tp-crown" dangerouslySetInnerHTML={{ __html: CROWN_SVG }} />}
            </button>
          );
        })}

        {!isResult && (
          <div className={`tp-abs tp-rule ${st === 'locked' || st === 'revealing' ? 'closed' : hint ? 'gold' : ''}`}>
            {st === 'locked' || st === 'revealing' ? 'BETS CLOSED' : 'HIGHEST HAND WINS · SAME HAND = STAKES BACK'}
          </div>
        )}

        <div className="tp-boxes">
          {SIDES.map((side) => (
            <button key={side} type="button" className={`tp-box tp-box--${side} ${sideCls(side)}`} {...press(() => place(side))}>
              <span className="tp-ignite" />
              <b>{SIDE_NAME[side]}</b>
              <i className="tp-num">{fmtX(PAYOUT)}</i>
              {hint && <small>{side === 'a' ? 'LEFT HAND' : 'RIGHT HAND'}</small>}
              <em className={`tp-num ${pill(side).cls}`}>{pill(side).text || '·'}</em>
            </button>
          ))}
        </div>

        {isResult && stampText && (
          <div className={`tp-abs tp-stamp tp-stamp--${winner} ${stampOut ? 'out' : ''}`}>{stampText.big}<small>{stampText.small}</small></div>
        )}
      </div>

      <div className={`tp-strip ${strip.cls}`} key={strip.text.replace(/\d+s$/, '')} {...press(() => setRulesOpen(true))}>{strip.text}</div>

      <div className="tp-dock">
        <div className={`tp-rail ${betting ? '' : 'closed'}`}>
          {CHIPS.map((c) => (
            <button key={c} type="button" data-v={c} className={`tp-chip ${chip === c ? 'sel' : ''}`} disabled={!walletReady || c > balance} aria-label={`₹${c} chip`}
                    {...press(() => { setChip(c); lsSet('tp.chip', String(c)); sTap(); })}>
              {c >= 1000 ? `${c / 1000}k` : c}
            </button>
          ))}
        </div>
        <div className="tp-sides">
          {SIDES.map((side) => (
            <button key={side} type="button" className={`tp-side tp-side--${side} ${sideCls(side)}`} {...press(() => place(side))}>
              <span className="tp-ignite" />
              <b>{SIDE_NAME[side]}</b>
              <i className="tp-num">{fmtX(PAYOUT)}</i>
              <em className={`tp-num ${pill(side).cls}`}>{pill(side).text || '·'}</em>
            </button>
          ))}
        </div>
        <div className="tp-tools">
          <button type="button" className="tp-pill" disabled={!betting || myTotal === 0} {...press(undoAll)}>↶ UNDO</button>
          <span className="tp-total tp-num">TOTAL {inr(myTotal)}</span>
          <button type="button" className={`tp-pill tp-pill--rebet ${rebetBlink ? 'blink' : ''}`} disabled={!walletReady || !lastBet || !betting || myTotal > 0} {...press(rebet)}>
            {lastBet ? `↻ REBET ${inr(lastBet.amount)}` : '↻ REBET'}
          </button>
        </div>
      </div>

      <div className="tp-ticker">
        <div className="tp-ticker-track" key={crowdRows.length ? crowdRows[0] : 'e'}>
          {[...crowdRows, ...crowdRows].map((t, i) => <span key={i} className="tp-ticker-item">{t.split(' put ')[0]} put <b>{t.split(' put ')[1]}</b></span>)}
          {crowdRows.length === 0 && <span className="tp-ticker-item">{seats} playing now · {mask('Tarun')} top take today ₹52,000</span>}
        </div>
      </div>

      {coins.length > 0 && (
        <div className="tp-coins" aria-hidden>
          {coins.map((b) => Array.from({ length: 8 }, (_, i) => (
            <span key={`${b.id}-${i}`} className="tp-coin" style={{ ['--tp-cx' as string]: `${b.cx}px`, ['--tp-cy' as string]: `${b.cy}px`, ['--tp-d' as string]: `${i * 45}ms`, ['--tp-jit' as string]: `${(i % 2 ? 1 : -1) * 8}px` }} />
          )))}
        </div>
      )}
      {hint && betting && <div className="tp-hint">👇 TAP A PLAYER</div>}
      {rulesOpen && (
        <>
          <div className="tp-backdrop" {...press(() => setRulesOpen(false))} />
          <div className="tp-sheet">
            <i />
            <h3>TEEN PATTI 20-20 — HOW TO PLAY</h3>
            <div>
              1. Two hands are dealt: PLAYER A (left) and PLAYER B (right). You have 15 seconds.<br />2. Pick a chip. Tap the hand you think wins. Tap again to add more.<br />3. Three cards each turn over one by one. The higher hand wins.
              <b>HAND RANKS (BEST FIRST)</b>TRAIL (three of a kind, A-A-A best) → PURE SEQUENCE (three in a row, same suit; A-K-Q best, then A-2-3) → SEQUENCE (three in a row) → COLOUR (same suit) → PAIR → HIGH CARD. Suits never decide. The ladder at the top shows where each hand stands as cards turn.
              <b>WHAT YOU GET BACK</b>Every X is your TOTAL RETURN. ₹100 on X1.98 → ₹198 back. Lose → your chips are gone. No bet → nothing changes.
              <b>SAME HAND = PUSH</b>If both hands rank exactly the same, nobody wins and your stake comes straight back.
              <b>ONE HAND PER ROUND</b>Tapping the other hand moves your whole bet there. UNDO takes back everything you placed this round.
              <b>WHEN THE TIMER HITS 0</b>Bets are closed. Nothing can be added or undone.
              <b>REBET</b>After a round, REBET puts the same money on the same hand in one tap.
              <b>FAIR DEAL</b>One 52-card deck, shuffled fresh every round, dealt A, B, A, B, A, B like a real table. Everyone at the table sees the same cards.
            </div>
            <button type="button" {...press(() => setRulesOpen(false))}>GOT IT</button>
          </div>
        </>
      )}
    </div>
    </div>
  );
};

export default TpPage;
